<template>
    <section>
        <h3>{{ layoutName }} for contact record</h3>

        <div class="description">
            The users listed below have access to view the “{{ layoutName }}” layout of the contact record. If one user is assigned
            to multiple layouts they will be able to select which layout they want to view on the contact record.
        </div>

        <div v-for="owner in owners" :key="owner.value" class="user-card-assigned">
            <ds-card>
                <ds-card-body class="user-column">
                    <div class="record-avatar">
                        <ds-avatar
                            data-qa="profile-image"
                            :name="owner.label"
                            :size="40"
                        />
                        {{ owner.label }}
                    </div>

                    <ds-outline-button data-qa="add-lineitem" @click="removeUser(owner)">
                        Unassign
                    </ds-outline-button>
                </ds-card-body>
            </ds-card>
        </div>

        <div class="input-row">
            <ds-dropdown class="dropdown">
                <ds-filled-button button-class="primary">
                    <ds-icon class="add-user-icon" name="add" />
                    Assign another user
                </ds-filled-button>

                <div slot="menu">
                    <div>
                        <ul>
                            <li
                                v-for="temp in ownersDrop"
                                :key="temp.value"
                                class="dropdown-menu-item"
                                @click="addUser(temp)"
                            >
                                <ds-avatar
                                    class="dropdown-avatar"
                                    data-qa="profile-image"
                                    :name="temp.label"
                                    :size="25"
                                />
                                {{ temp.label }}
                            </li>
                        </ul>
                    </div>

                    <div
                        slot="footer"
                        class="footer"
                        @click="viewMangeUser"
                    >
                        <a class="manage-user">Manage user</a>
                    </div>
                </div>
            </ds-dropdown>
        </div>
    </section>
</template>
<script>
import axios from 'axios';
import cloneDeep from 'lodash.clonedeep';

export default {

    props: {
        userId: [String, Number],
        layoutData: Object,
    },

    data() {
        return {
            options: {},
            owners: [],
            ownersCopy: [],
            toastMessage: '',
        };
    },

    created() {
        this.fetchDropdowns();
    },

    computed: {
        ownersDrop() {
            return this.options.owners;
        },

        layoutName() {
            return this.layoutData?.contactCustomizableLayout?.name;
        },
    },

    methods: {
        removeUser(remUser) {
            const index = this.owners?.findIndex((usr) => Number(usr.value) === Number(remUser.value));

            if (index !== -1) {
                this.owners?.splice(index, 1);
            }
            this.toastMessage = 'Removing user';
            this.checkIfRemovedLayoutIsDefaultForUserId(remUser.value, this.layoutData?.contactCustomizableLayout?.id);

            if (Number(remUser.value) === Number(this.userId)) {
                this.$root.$emit('LAYOUT_LIST_DISPLAY', 'default');
            }
        },

        async checkIfRemovedLayoutIsDefaultForUserId(removableUserId, layoutId) {
            try {
                await axios.put(`/app/contact/verifyLayoutByUserId/${removableUserId}/${layoutId}`);
                this.removeUserForLayout(removableUserId, layoutId);
            } catch (e) {
                this.$error({ message: 'Unable to set default layout after removing' });
            }
        },

        viewMangeUser() {
            window.open('/app/nav/link?navSystem=nav.admin&navModule=user-profile');
        },

        addUser(data) {
            this.owners.push(data);
            this.toastMessage = 'Added user';
            this.saveAddedUsers(data);
        },

        async removeUserForLayout(removableUserId, layoutId) {
            try {
                await axios.delete(`/app/contactLayout/removeUser/${layoutId}/${removableUserId}`);
                this.getUsers();
                this.$toast({ message: 'removed user successfully' });
            } catch (error) {
                this.$error({ message: 'error occurred while removing user' });
            }
        },

        async saveAddedUsers(data) {
            try {
                const contactLayoutDto = {};

                contactLayoutDto.id = this.layoutData?.contactCustomizableLayout?.id;
                const usersList = [];

                usersList.push(Number(data.value));
                contactLayoutDto.assignedUsers = usersList;
                await axios.put('/app/contactLayout', contactLayoutDto);
                this.getUsers();
                this.$toast({ message: `${this.toastMessage} successfully` });
            } catch (error) {
                this.$error({ message: `error occurred while ${this.toastMessage}` });
            }
        },

        getUsers() {
            this.options.owners = cloneDeep(this.ownersCopy);
            const addedUsers = this.owners?.map((x) => x.value);

            for (const user in addedUsers) {
                if ([...addedUsers][user]) {
                    const fieldInd = this.options?.owners?.findIndex((x) => x.value === addedUsers[user]);

                    if (fieldInd !== -1) {
                        this.options?.owners?.splice(fieldInd, 1);
                    }
                }
            }

            this.$root.$emit('ADDED_USERS_COUNT', { count: addedUsers.length, name: this.layoutData?.contactCustomizableLayout?.name });
        },

        async fetchDropdowns() {
            try {
                const response = await axios.get('/app/contact/loadDropdowns', {
                    headers: {
                        Accept: 'application/json',
                    },
                });

                this.options = response.data;
                const ownersDrop = [];

                this.options.owners = Object.entries(this.options.owners).map((key) => {
                    ownersDrop.push({ label: key[1], value: Number(key[0]) });

                    return { label: key[1], value: Number(key[0]) };
                });

                this.options.owners = ownersDrop;
                this.ownersCopy = cloneDeep(ownersDrop);

                const usersFromLayout = this.layoutData?.contactCustomizableLayout?.assignedUsers?.map(Number);

                for (const fild in usersFromLayout) {
                    if ([...usersFromLayout][fild]) {
                        const find = this.ownersCopy.find((x) => x.value === usersFromLayout[fild]);

                        this.owners.push(find);
                    }
                }
                this.getUsers();
            } catch (e) {
                this.$error({ message: 'Unable to load all dropdowns' });
            }
        },

    },
};
</script>
<style lang="scss" scoped>
    .user-card-assigned{
        padding: $spacing-100;
    }

    .user-column{
        display: inline-flex;
        width: 100%;
        justify-content: space-between;
    }

    .footer {
        position: sticky;
        bottom: 0;
        background-color: $color-white;
        height: 3.5rem;
        justify-content: center;
    }

    .manage-user {
        text-decoration: none;
    }

    .input-row {
        --dropdown-maxwidth: #{px-to-rem(200px)};
        --dropdown-height: #{px-to-rem(200px)};
        margin: $spacing-200;
    }

    .add-user-icon{
        --icon-size: 1rem;
        margin-bottom: - $spacing-050;
    }

    .description {
        padding: $spacing-200 0;
    }

    .dropdown-avatar{
        margin-right: $spacing-100;
    }
</style>
